import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

const POLL_MS = 5000;
// Games this tab left with "Return to lobby": no pulling back in.
export const LIVE_LEFT_KEY = "jangle.live.left";

export function markLiveGameLeft(matchId) {
  try {
    const left = JSON.parse(sessionStorage.getItem(LIVE_LEFT_KEY) || "[]");
    if (!left.includes(matchId)) sessionStorage.setItem(LIVE_LEFT_KEY, JSON.stringify([...left, matchId].slice(-20)));
  } catch { /* ignore */ }
}

function leftGames() {
  try { return JSON.parse(sessionStorage.getItem(LIVE_LEFT_KEY) || "[]"); } catch { return []; }
}

// A Live Game you challenged someone to that hasn't started yet, because they
// accepted your challenge while you were somewhere else (Jack, 2026-11-02:
// "she accepted but the game never appeared on my side"). From the lobby
// it opens straight away; on any other page a pop-up offers Join.
export default function LiveGameJoin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pathRef = useRef(pathname);
  pathRef.current = pathname;
  const userId = user?.id;
  // Join pop-ups showing now, by match id.
  const shownRef = useRef(new Set());
  const dismiss = useCallback((matchId) => {
    toast.dismiss(`live-${matchId}`);
    shownRef.current.delete(matchId);
  }, []);
  // Into the game (or any game): its pop-up goes.
  useEffect(() => {
    for (const id of [...shownRef.current]) {
      if (pathname.startsWith(`/match/${id}`)) dismiss(id);
    }
  }, [pathname, dismiss]);

  const check = useCallback(async () => {
    if (document.visibilityState === "hidden") return;
    let games;
    try {
      ({ data: { games } } = await api.get("/me/live-waiting"));
    } catch {
      return;
    }
    const left = leftGames();
    // A game that has started (or ended) since: its pop-up goes.
    const waitingIds = new Set((games || []).map((g) => g.match_id));
    for (const id of [...shownRef.current]) {
      if (!waitingIds.has(id) || pathRef.current.startsWith(`/match/${id}`)) dismiss(id);
    }
    for (const g of games || []) {
      if (left.includes(g.match_id)) continue;
      const path = `/match/${g.match_id}`;
      if (pathRef.current.startsWith(path)) continue;
      const url = `${path}?layout=${g.layout || "classic"}`;
      if (pathRef.current === "/lobby" || pathRef.current === "/") {
        navigate(url);
        return;
      }
      shownRef.current.add(g.match_id);
      toast(`${g.opponent || "Your opponent"} is ready for your Live Game`, {
        id: `live-${g.match_id}`,
        description: "It starts when you join.",
        duration: Infinity,
        action: { label: "Join", onClick: () => { shownRef.current.delete(g.match_id); navigate(url); } },
        cancel: { label: "Later", onClick: () => { shownRef.current.delete(g.match_id); markLiveGameLeft(g.match_id); } },
      });
      return;
    }
  }, [navigate, dismiss]);

  useEffect(() => {
    if (!userId) return undefined;
    check();
    const t = setInterval(check, POLL_MS);
    document.addEventListener("visibilitychange", check);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", check);
    };
  }, [userId, check]);

  return null;
}
